import React, { useCallback, useEffect, useMemo } from 'react';
import {
  AuthProvider as OidcAuthProvider,
  AuthContext,
  type AuthContextProps,
} from 'react-oidc-context';
import type {
  QuerySessionStatusArgs,
  RevokeTokensTypes,
  SessionStatus,
  SigninPopupArgs,
  SigninRedirectArgs,
  SigninResourceOwnerCredentialsArgs,
  SigninSilentArgs,
  SignoutPopupArgs,
  SignoutRedirectArgs,
  SignoutSilentArgs,
  User,
  UserManagerEvents,
  UserManagerSettings,
} from 'oidc-client-ts';
import type { KeycloakConfig } from '../config/types';
import { KeycloakConfigBuilder } from '../config/KeycloakConfig';
import { TokenLifecycleManager } from './TokenLifecycleManager';
import { SessionMonitor } from './SessionMonitor';
import { TokenBridgeSync } from './TokenBridge';
import { mergeWithPreset, type ConfigPreset } from '../config/presets';
import { defaultConfig } from '../config/defaults';
import { StorageConfigProvider } from '../contexts/StorageConfigContext';
import {
  KeycloakStatusProvider,
  type KeycloakStatus,
} from '../contexts/KeycloakStatusContext';
import type { TokenBridge } from '../utils/tokenBridge';

const createDisabledEvents = (): UserManagerEvents => {
  const noop = () => undefined;
  const noopSubscription = () => () => undefined;
  const noopAsync = async () => undefined;

  const events: Partial<UserManagerEvents> = {
    addAccessTokenExpiring: noopSubscription,
    removeAccessTokenExpiring: noop,
    addAccessTokenExpired: noopSubscription,
    removeAccessTokenExpired: noop,
    addSilentRenewError: noopSubscription,
    removeSilentRenewError: noop,
    addUserLoaded: noopSubscription,
    removeUserLoaded: noop,
    addUserUnloaded: noopSubscription,
    removeUserUnloaded: noop,
    addUserSignedIn: noopSubscription,
    removeUserSignedIn: noop,
    addUserSignedOut: noopSubscription,
    removeUserSignedOut: noop,
    addUserSessionChanged: noopSubscription,
    removeUserSessionChanged: noop,
    load: noopAsync,
    unload: noopAsync,
  };

  return events as UserManagerEvents;
};

const disabledEvents = createDisabledEvents();

const disabledSettings: UserManagerSettings = {
  authority: '',
  client_id: '',
  redirect_uri: 'http://localhost/disabled',
  response_type: 'code',
  scope: '',
};

const disabledError = () =>
  new Error('Keycloak integration is disabled for this environment.');

const disabledAsync = async <T,>(): Promise<T> => {
  throw disabledError();
};

const disabledAuthContext: AuthContextProps = {
  settings: disabledSettings,
  events: disabledEvents,
  clearStaleState: async (): Promise<void> => undefined,
  removeUser: async (): Promise<void> => undefined,
  signinPopup: (_args?: SigninPopupArgs) => disabledAsync<User>(),
  signinSilent: async (_args?: SigninSilentArgs): Promise<User | null> => null,
  signinRedirect: (_args?: SigninRedirectArgs) => disabledAsync<void>(),
  signinResourceOwnerCredentials: (
    _args: SigninResourceOwnerCredentialsArgs
  ) => disabledAsync<User>(),
  signoutRedirect: async (_args?: SignoutRedirectArgs): Promise<void> =>
    undefined,
  signoutPopup: async (_args?: SignoutPopupArgs): Promise<void> => undefined,
  signoutSilent: async (_args?: SignoutSilentArgs): Promise<void> => undefined,
  querySessionStatus: async (
    _args?: QuerySessionStatusArgs
  ): Promise<SessionStatus | null> => null,
  revokeTokens: async (_types?: RevokeTokensTypes): Promise<void> => undefined,
  startSilentRenew: (): void => undefined,
  stopSilentRenew: (): void => undefined,
  user: null,
  isLoading: false,
  isAuthenticated: false,
  activeNavigator: undefined,
  error: undefined,
};

/**
 * Props for zero-config mode (simplified setup)
 */
export interface KeycloakProviderZeroConfigProps {
  /**
   * Keycloak realm authority URL (e.g., https://sso.example.com/realms/myrealm)
   */
  authority: string;

  /**
   * Client ID registered in Keycloak
   */
  clientId: string;

  /**
   * Configuration preset to use
   * @default 'default'
   */
  preset?: ConfigPreset;

  /**
   * Toggle to disable Keycloak integration entirely.
   * When false, children render with a stub auth context.
   * @default true
   */
  enabled?: boolean;

  /**
   * Child components to render
   */
  children: React.ReactNode;

  /**
   * Enable debug logging to console
   * @default false
   */
  debug?: boolean;

  /**
   * Override specific configuration properties
   * Use this to customize preset or zero-config defaults
   */
  config?: Partial<Omit<KeycloakConfig, 'authority' | 'clientId'>>;

  /**
   * Optional token bridge instance for exposing tokens outside React.
   */
  tokenBridge?: TokenBridge;
}

/**
 * Props for full-config mode (advanced setup)
 */
export interface KeycloakProviderFullConfigProps {
  /**
   * Full Keycloak configuration object
   */
  config: KeycloakConfig;

  /**
   * Toggle to disable Keycloak integration entirely.
   * When false, children render with a stub auth context.
   * @default true
   */
  enabled?: boolean;

  /**
   * Child components to render
   */
  children: React.ReactNode;

  /**
   * Enable debug logging to console
   * @default false
   */
  debug?: boolean;

  /**
   * Optional token bridge instance for exposing tokens outside React.
   */
  tokenBridge?: TokenBridge;
}

/**
 * Combined props type
 */
export type KeycloakProviderProps = KeycloakProviderZeroConfigProps | KeycloakProviderFullConfigProps;

/**
 * Type guard to check if using zero-config mode
 */
function isZeroConfig(props: KeycloakProviderProps): props is KeycloakProviderZeroConfigProps {
  return 'authority' in props && 'clientId' in props;
}

/**
 * Main Keycloak authentication provider component
 *
 * This component wraps your application to provide authentication context using Keycloak.
 * It supports both zero-config mode (for simplicity) and full-config mode (for advanced use cases).
 *
 * ## Zero-Config Mode (Recommended)
 *
 * Minimal setup with sensible defaults and automatic feature enablement.
 *
 * @example Zero-config (simplest)
 * ```tsx
 * import { KeycloakProvider } from 'antaeus.keycloak.react';
 *
 * function App() {
 *   return (
 *     <KeycloakProvider
 *       authority="https://sso.example.com/realms/myrealm"
 *       clientId="my-app"
 *     >
 *       <YourApp />
 *     </KeycloakProvider>
 *   );
 * }
 * ```
 *
 * @example Zero-config with preset
 * ```tsx
 * <KeycloakProvider
 *   authority="https://sso.example.com/realms/myrealm"
 *   clientId="my-app"
 *   preset="mobile"  // or 'high-security', 'long-running', 'development'
 * >
 *   <YourApp />
 * </KeycloakProvider>
 * ```
 *
 * @example Zero-config with overrides
 * ```tsx
 * <KeycloakProvider
 *   authority="https://sso.example.com/realms/myrealm"
 *   clientId="my-app"
 *   preset="default"
 *   config={{
 *     silentRenew: {
 *       maxRefreshTokenLifetime: 604800, // Override: 7 days instead of 30
 *       showLoadingIndicator: true,
 *       useRefreshToken: true, // Explicitly ensure refresh tokens are used when available
 *     }
 *   }}
 *   debug={true}
 * >
 *   <YourApp />
 * </KeycloakProvider>
 * ```
 *
 * @example Using localStorage to persist sessions across tabs/windows
 * ```tsx
 * <KeycloakProvider
 *   authority="https://sso.example.com/realms/myrealm"
 *   clientId="my-app"
 *   config={{
 *     advanced: {
 *       storageType: 'local', // Use localStorage instead of sessionStorage
 *     }
 *   }}
 * >
 *   <YourApp />
 * </KeycloakProvider>
 * ```
 *
 * ## Full-Config Mode (Advanced)
 *
 * Complete control over all configuration options.
 *
 * @example Full configuration
 * ```tsx
 * <KeycloakProvider
 *   config={{
 *     authority: 'https://sso.example.com/realms/myrealm',
 *     clientId: 'my-app',
 *     redirectUri: window.location.origin,
 *     scope: 'openid profile email offline_access',
 *     features: {
 *       autoRefresh: true,
 *       sessionMonitor: true,
 *     },
 *     silentRenew: {
 *       enabled: true,
 *       refreshStrategy: 'both',
 *       maxRefreshTokenLifetime: 2592000,
 *       useRefreshToken: true,
 *     },
 *     events: {
 *       onLogin: (user) => console.log('User logged in', user),
 *       onError: (error) => sendToSentry(error),
 *     }
 *   }}
 *   debug={true}
 * >
 *   <YourApp />
 * </KeycloakProvider>
 * ```
 *
 * ## Features Automatically Enabled
 *
 * When using zero-config or presets, the following features are automatically enabled:
 * - ✅ Automatic token refresh (before expiration)
 * - ✅ Token lifecycle management (refresh on app startup)
 * - ✅ Session monitoring UI (countdown timer)
 * - ✅ Silent renew (background token refresh)
 * - ✅ Refresh token grant fallback on app restart
 * - ✅ Error notifications (user-friendly messages)
 * - ✅ 401 retry logic (automatic token refresh on API errors)
 * - ✅ Maximum refresh token lifetime (security control)
 * - ✅ PKCE (for secure public clients)
 *
 * All features can be disabled or customized via configuration overrides.
 */
export const KeycloakProvider: React.FC<KeycloakProviderProps> = (props) => {
  const { children, debug = false } = props;

  const status: KeycloakStatus = useMemo(() => {
    const enabledFlag = props.enabled ?? true;
    const hasAuthority = isZeroConfig(props)
      ? Boolean(props.authority)
      : Boolean(props.config.authority);
    const hasClientId = isZeroConfig(props)
      ? Boolean(props.clientId)
      : Boolean(props.config.clientId);

    return {
      isEnabled: enabledFlag,
      isConfigured: hasAuthority && hasClientId,
      reason: !enabledFlag
        ? 'disabled'
        : !hasAuthority || !hasClientId
          ? 'missing-config'
          : undefined,
    };
  }, [props]);

  const effectiveEnabled = status.isEnabled && status.isConfigured;

  useEffect(() => {
    if (!effectiveEnabled && props.tokenBridge) {
      props.tokenBridge.setToken(null);
    }
  }, [effectiveEnabled, props.tokenBridge]);

  if (!effectiveEnabled) {
    return (
      <KeycloakStatusProvider value={status}>
        <AuthContext.Provider value={disabledAuthContext}>
          <StorageConfigProvider storageType="session">
            {children}
          </StorageConfigProvider>
        </AuthContext.Provider>
      </KeycloakStatusProvider>
    );
  }

  // Build configuration based on mode
  const config: KeycloakConfig = isZeroConfig(props)
    ? buildZeroConfig(props)
    : props.config;

  // Build OIDC configuration from Keycloak config
  const oidcConfig = KeycloakConfigBuilder.build(config);

  // Logging helper
  const log = useCallback(
    (level: 'info' | 'warn' | 'error', message: string, ...args: any[]) => {
      if (debug) {
        const prefix = '[KeycloakProvider]';
        switch (level) {
          case 'info':
            console.log(prefix, message, ...args);
            break;
          case 'warn':
            console.warn(prefix, message, ...args);
            break;
          case 'error':
            console.error(prefix, message, ...args);
            break;
        }
      }
    },
    [debug]
  );

  // Event handlers with built-in functionality
  const onSigninCallback = useCallback(
    (_user: User | void): void => {
      log('info', 'Sign in successful');

      // Clean up query parameters from URL after redirect
      window.history.replaceState({}, document.title, window.location.pathname);

      // Call user-provided callback
      if (_user) {
        config.events?.onLogin?.(_user as User);
      }
    },
    [config.events, log]
  );

  const onSignoutCallback = useCallback((): void => {
    log('info', 'Sign out successful');

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    // Call user-provided callback
    config.events?.onLogout?.();
  }, [config.events, log]);

  const onRemoveUser = useCallback((): void => {
    log('warn', 'User removed from storage - token validation likely failed');
  }, [log]);



  // Check if SessionMonitor should be shown
  const showSessionMonitor = config.features?.sessionMonitor !== false;

  // Get storage type from config
  const storageType = config.advanced?.storageType || 'session';

  return (
    <KeycloakStatusProvider value={status}>
      <OidcAuthProvider
        {...oidcConfig}
        onSigninCallback={onSigninCallback}
        onSignoutCallback={onSignoutCallback}
        onRemoveUser={onRemoveUser}
      >
        <StorageConfigProvider storageType={storageType}>
          <TokenLifecycleManager config={config} debug={debug}>
            {props.tokenBridge && (
              <TokenBridgeSync bridge={props.tokenBridge} />
            )}
            {/* Auto-include SessionMonitor if enabled */}
            {showSessionMonitor && <SessionMonitor />}

            {/* User's app content */}
            {children}
          </TokenLifecycleManager>
        </StorageConfigProvider>
      </OidcAuthProvider>
    </KeycloakStatusProvider>
  );
};

/**
 * Build configuration for zero-config mode
 */
function buildZeroConfig(props: KeycloakProviderZeroConfigProps): KeycloakConfig {
  const { authority, clientId, preset = 'default', config: overrides = {} } = props;

  // Start with preset configuration
  const presetConfig = mergeWithPreset(preset, overrides);

  // Deep merge: defaults -> preset -> overrides
  const fullConfig: KeycloakConfig = {
    authority,
    clientId,
    redirectUri: presetConfig.redirectUri || defaultConfig.redirectUri || (typeof window !== 'undefined' ? window.location.origin + '/' : '/'),
    postLogoutRedirectUri: presetConfig.postLogoutRedirectUri || defaultConfig.postLogoutRedirectUri || (typeof window !== 'undefined' ? window.location.origin + '/' : '/'),
    scope: presetConfig.scope || defaultConfig.scope,
    features: {
      ...defaultConfig.features,
      ...presetConfig.features,
    },
    silentRenew: {
      ...defaultConfig.silentRenew,
      ...presetConfig.silentRenew,
    },
    advanced: {
      ...defaultConfig.advanced,
      ...presetConfig.advanced,
    },
    events: {
      ...defaultConfig.events,
      ...presetConfig.events,
    },
  };

  return fullConfig;
}
